import React from 'react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { usePreventSpaceKey } from '@/hooks/usePreventSpaceKey';
import CallsDetailOverviewWidget from '@/components/widgets/call-overview/CallsDetailOverviewWidget';
import { CallsOverviewTableColumns } from '@/enums/call-overvew-column';

interface TransferredRateBadgeProps {
  agentName?: string;
  queueName?: string;
  transferredCount: number;
  totalCount: number;
}

export const TransferredRateBadge: React.FC<TransferredRateBadgeProps> = ({
  agentName,
  queueName,
  transferredCount,
  totalCount
}) => {
  return (
    <div className="flex items-center gap-2">
      {(agentName || queueName) && (
        <Badge variant="outline" className="ml-2 font-normal text-xs text-inherit">
          {agentName} - {queueName}
        </Badge>
      )}
      <Badge variant="outline" className="font-normal text-xs text-inherit">
        {transferredCount}/{totalCount} total calls
      </Badge>
    </div>
  );
};

interface TransferredRateModalProps {
  isOpen: boolean;
  onClose: () => void;
  dateParams?: {
    startDate: string;
    endDate: string;
    previousStartDate: string;
    previousEndDate: string;
    queueMicrosoftIds?: string[] | null;
    agentMicrosoftIds?: string[] | null;
    groupIds?: number[] | null;
  };
  transferredCallIds?: string[];
  handledCallIds?: string[];
  agentName?: string;
  queueName?: string;
}

export const TransferredRateModal: React.FC<TransferredRateModalProps> = ({
  isOpen,
  onClose,
  dateParams,
  transferredCallIds = [],
  handledCallIds = [],
  agentName,
  queueName,
}) => {
  // Prevent space key from triggering default behavior
  usePreventSpaceKey(isOpen);

  const transferredCount = transferredCallIds.length;
  const totalCount = transferredCallIds.length + handledCallIds.length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-[95vw] max-h-[90vh] overflow-auto p-0"
        closeClassName="z-50"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {dateParams ? (
          <CallsDetailOverviewWidget
            callDirectionTypes={null}
            dateParams={dateParams}
            limit={10}
            callMicrosoftIds={transferredCallIds}
            hiddenColumns={[
              CallsOverviewTableColumns.CALL_QUEUE,
              CallsOverviewTableColumns.CALL_STATUS,
              CallsOverviewTableColumns.CALL_DURATION,
              CallsOverviewTableColumns.CALLER,
              CallsOverviewTableColumns.CUSTOMER_NAME,
              CallsOverviewTableColumns.COMPANY_NAME,
              CallsOverviewTableColumns.HUNTED_AGENT,
              CallsOverviewTableColumns.CALLEE,
              CallsOverviewTableColumns.PARTICIPANTS,
              CallsOverviewTableColumns.VOICEMAIL_DURATION
            ]}
            exportHiddenColumns={[
              CallsOverviewTableColumns.CALL_STATUS,
              CallsOverviewTableColumns.CUSTOMER_NAME,
              CallsOverviewTableColumns.COMPANY_NAME,
              CallsOverviewTableColumns.HUNTED_AGENT,
              CallsOverviewTableColumns.CALLEE
            ]}
            exportButtonClassName="mr-10"
            hideTooltips={true}
            agentName={agentName}
            queueName={queueName}
            agentDisplayNameTransfer={agentName ?? null}
            customBadge={
              <TransferredRateBadge
                agentName={agentName}
                queueName={queueName}
                transferredCount={transferredCount}
                totalCount={totalCount}
              />
            }
          />
        ) : (
          <div className="text-sm text-muted-foreground py-4">
            No date parameters provided
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
